import React, { useEffect } from "react";
import { useDispatch, useSelector } from "react-redux";
import Banner from "../components/Header/Banner";
import Foods from "../components/Foods Showcase/Foods";
import Footer from "../components/Footer/Footer";
import StoreClosed from "../components/StoreClosed/StoreClosed";
import NetworkError from "../components/NetworkError/NetworkError";
import { fetchProfile } from "../redux/menu/ProfileSlice";
import { fetchCategories } from "../redux/menu/CategoriesSlice";
import { isStoreOpen } from "../utils/storeTimeUtils";
import { useNetworkError } from "../hooks/useNetworkError";

export default function HomeScreen() {
  const dispatch = useDispatch();
  const { profile, status: profileStatus, error: profileError } = useSelector((state) => state.profile);
  const { products, status: productsStatus, error: productsError, isSearchMode } = useSelector((state) => state.product);
  const { showNetworkError, errorMessage, handleNetworkError, retryAction } = useNetworkError();

  // Fetch profile details when component mounts
  useEffect(() => {
    dispatch(fetchProfile());
  }, [dispatch]);

  // Fetch categories when component mounts
  useEffect(() => {
    if (profile?.id) {
      dispatch(fetchCategories(profile.id));
    }
  }, [dispatch, profile?.id]);

  // Handle network errors from profile fetch
  useEffect(() => {
    if (profileStatus === 'failed' && profileError?.type === 'NETWORK_ERROR') {
      handleNetworkError(profileError, profileError.message);
    }
  }, [profileStatus, profileError, handleNetworkError]);

  // Handle network errors from products fetch
  useEffect(() => {
    if (productsStatus === 'failed' && productsError?.type === 'NETWORK_ERROR') {
      handleNetworkError(productsError, productsError.message);
    }
  }, [productsStatus, productsError, handleNetworkError]);

  // Retry function for network errors
  const handleRetry = () => {
    retryAction(() => {
      dispatch(fetchProfile());
      if (profile?.id) {
        dispatch(fetchCategories(profile.id));
      }
    });
  };


  // Check if store is currently open
  const storeIsOpen = isStoreOpen(profile?.from_time, profile?.to_time);
  
  // Check if store status is available (not false)
  const storeStatusAvailable = profile?.status !== false;

  // If store is closed or status is false, show the StoreClosed component
  if ((!storeIsOpen && profile?.from_time && profile?.to_time) || !storeStatusAvailable) {
    return <StoreClosed />;
  }

  return (
    <div className="relative min-h-screen bg-white overflow-hidden">
      {/* Watermark background image */}
      <div
        className="absolute inset-0 z-0"
        style={{
          backgroundImage: "url('https://images.unsplash.com/photo-1504674900247-0877df9cc836?auto=format&fit=crop&w=1200&q=80')",
          backgroundSize: "cover",
          backgroundPosition: "center",
          opacity: 0.04, // Extremely subtle opacity for watermark effect
          pointerEvents: "none"
        }}
      />
      {/* Main content */}
      <div className="relative">
        <Banner />
        
        {/* Search Results Header */}
        {isSearchMode && productsStatus === 'succeeded' && products && products.length > 0 && (
          <div className="container mx-auto px-4 pt-8">
            <div className="text-center mb-8">
              <div className="inline-flex items-center justify-center bg-gradient-to-r from-primary/10 to-primary/5 px-6 py-3 rounded-full border border-primary/20">
                <span className="text-primary font-semibold text-lg">
                  Found {products.length} delicious item{products.length !== 1 ? 's' : ''}
                </span>
              </div>
            </div>
          </div>
        )}
        
        {/* Search Results - No Products Found */}
        {isSearchMode && productsStatus === 'succeeded' && products && products.length === 0 && (
          <div className="text-center py-12">
            <div className="text-gray-500 text-lg mb-4">No products found</div>
            <p className="text-gray-400">Try searching with different keywords</p>
          </div>
        )}
        
        {/* Search Error */}
        {isSearchMode && productsStatus === 'failed' && (
          <div className="text-center py-12">
            <div className="text-red-500 text-lg mb-4">Search failed</div>
            <p className="text-gray-400">Error: {productsError}</p>
          </div>
        )}
        
        {/* Always use Foods component to display products (regular or search results) */}
        <Foods />
        
        <Footer />
      </div>
      
      {/* Network Error Modal */}
      {showNetworkError && (
        <NetworkError 
          message={errorMessage}
          onRetry={handleRetry}
        />
      )}
    </div>
  );
}
